/* @flow */
import React from 'react';
import cx from 'classnames';
import { connect } from 'react-redux';
import Activity from 'entities/Activity';
import ActivityRecipient from 'entities/ActivityRecipient';
import Button from 'components/Button.jsx';
import SimpleDateIcon from 'components/SimpleDateIcon.jsx';
import Glyph from 'components/Glyph.jsx';
import Money from 'payments/components/Money.jsx';
import ActivityConversationHeaderManageButton from 'chat/components/ActivityConversationHeaderManageButton.jsx';
import Link from 'components/Link.jsx';
import RSVPDialogContainer from 'payments/containers/RSVPDialogContainer.jsx';
import RSVPDropdownContainer from 'payments/containers/RSVPDropdownContainer.jsx';
import { mountModal } from 'lib/modal';
import Text from 'components/Text.jsx';
import MediaObject from 'components/MediaObject.jsx';
import TimeRange from 'components/TimeRange.jsx';

import type {Attendance} from 'entities/ActivityRecipient'; // eslint-disable-line
import type { Sort } from 'payments/components/ActivityResponsesTable.jsx';

import './styles/ActivityConversationHeader.scss';

type Props = {
  activity: Activity,
  className?: ?string,
  onSideBarToggle: () => void,
  activityRecipient?: ?ActivityRecipient,
};

export class ActivityConversationHeader extends React.Component {
  props: Props;

  static contextTypes = {
    router: React.PropTypes.object.isRequired,
  };

  onRSVP: Function = (rsvp: ?Attendance): void => {
    mountModal(
      <RSVPDialogContainer
        initialRSVP={rsvp}
        activityId={this.props.activity.id}
      />
    , this);
  }

  getResponseURL: Function = (sort: Sort): string => {
    return `activities/${this.props.activity.id}?sort=${sort}`;
  }

  renderRsvpSelector() {
    const { activity } = this.props;

    if (activity.isClosed()) {
      return <span />;
    }

    return (
      <RSVPDropdownContainer activity={activity} onRSVPChange={this.onRSVP} data-test="rsvp-dropdown-container" />
    );
  }

  renderHeroImageSection() {
    const { activity } = this.props;
    const cover = activity.getCover();

    const style = {
      backgroundImage: `url(${cover.image})`,
      backgroundSize: 'auto 100px',
      backgroundPosition: 'top center',
    };

    return (
      <div className="ActivityConversationHeader-hero" style={style} />
    );
  }

  renderSenderSubtitle() {
    const { activity } = this.props;
    const summary = activity.getRecipientSummary();

    return (
      <div className="ActivityConversationHeader-rsvpInformation">
        <Link to={this.getResponseURL('yes')} color="eclipse">
          <span>{summary.getAttendingCount()} Yes</span>
        </Link>
        <span className="ActivityConversationHeader-separator">|</span>
        <Link to={this.getResponseURL('maybe')} color="eclipse">
          <span>{summary.getMaybeCount()} Maybe</span>
        </Link>
        <span className="ActivityConversationHeader-separator">|</span>
        <Link to={this.getResponseURL('no')} color="eclipse">
          <span>{summary.getNotAttendingCount()} No</span>
        </Link>
        {!activity.isClosed() && activity.isOrHasCollectedMoneyOnline() &&
          <span>
            <span className="ActivityConversationHeader-separator">|</span>
            <span className="ActivityConversationHeader-subSection-header-moneyCollected">
              <Money cents={activity.getTotalCollected()} /> Collected
            </span>
          </span>
        }
      </div>
    );
  }

  renderParticipantSubtitle() {
    const { activity } = this.props;
    const text = activity.isClosed() ? 'Activity is closed' : 'Organized by ' + activity.getRequester().getDisplayName();
    return <div>{text}</div>;
  }

  renderRespondedSection() {
    const { activity } = this.props;
    return (
      <div className="ActivityConversationHeader-subSection-infos">
        {!activity.isSender() && this.renderRsvpSelector()}
        <ActivityConversationHeaderManageButton
          activity={activity}
          size="xsmall"
          level="secondary"
        />
        <Button
          size="xsmall"
          level="secondary"
          onClick={this.props.onSideBarToggle}>
          <Glyph name="ellipses" solo />
        </Button>
      </div>
    );
  }

  renderUnRespondedSection() {
    const { activity } = this.props;
    const startAt = activity.getStartAt();
    const cost = activity.isFree() ? <span> Free </span> : <Money cents={activity.getAmountPerPerson()} />;
    const location = activity.getLocation();
    return (
      <div className="ActivityConversationHeader-summaryContainer" data-test="rsvp-unresponded-section">
        <div className="ActivityConversationHeader-summary">
          {
            startAt &&
              <MediaObject className="ActivityConversationHeader-summary-time" gutter="small">
                <Glyph color="thunder" name="calendar" />
                <Text color="thunder">
                  <TimeRange
                    start={startAt}
                    end={activity.getEndAt()}
                    month="long"
                    year="numeric"
                    day="numeric"
                    hour="numeric"
                    minute="numeric"
                  />
                </Text>
              </MediaObject>
          }
          <MediaObject className="ActivityConversationHeader-summary-cost" gutter="small">
            <Glyph name="dollar-circle" color="thunder" />
            <Text element="span" color="thunder">
              {cost}
            </Text>
          </MediaObject>
        </div>
        {
          location && (
            <MediaObject gutter="small">
              <Glyph color="thunder" name="location-pin" />
              <Text className="ActivityConversationHeader-summary-location" color="thunder" truncate>
                {activity.getLocation()}
              </Text>
            </MediaObject>
          )
        }
      </div>
    );
  }

  renderSubSection() {
    const { activity, activityRecipient } = this.props;
    const startAt = activity.getStartAt();
    let infoSection;
    if (activityRecipient) {
      infoSection = activityRecipient.getAttendance() === 'pending' ?
        this.renderUnRespondedSection() :
        this.renderRespondedSection();
    } else if (activity.isSender()) {
      infoSection = this.renderRespondedSection();
    }


    return (
      <div className="ActivityConversationHeader-subSection">
        <div className="ActivityConversationHeader-subSection-dateAndTitle">
          {startAt &&
            <SimpleDateIcon
              className="ActivityConversationHeader-date"
              date={startAt}
            />
          }
          <div className="ActivityConversationHeader-subSection-header">
            <div className="ActivityConversationHeader-subSection-header-title">
              {activity.getTitle()}
            </div>
            <div className="ActivityConversationHeader-subSection-header-subtitle">
              {activity.isSender() ? this.renderSenderSubtitle() : this.renderParticipantSubtitle()}
            </div>
          </div>
        </div>
        <div>
          {infoSection}
        </div>
      </div>
    );
  }

  render() {
    return (
      <div className={cx('ActivityConversationHeader', this.props.className)}>
        {this.renderHeroImageSection()}
        {this.renderSubSection()}
      </div>
    );
  }
}

function mapStateToProps(state: Object, props: Object) {
  const activity = props.chat.getActivity();
  if (activity && activity.id) {
    const activityObj = state.activityRecipients.activities[activity.id] || {};
    return {
      activityRecipient: activityObj.currentUserRecipient,
    };
  }
  return {};
}

export default connect(
  mapStateToProps,
)(ActivityConversationHeader);



// WEBPACK FOOTER //
// ./src/chat/components/ActivityConversationHeader.jsx